<div class="temporal-panel">
  <!-- Active state — prominent when the mode is on -->
  @if (temporal.active()) {
    <div class="active-section">
      <span class="section-label">{{ 'temporal.panel.viewing' | translate }}</span>
      <div class="active-instant">{{ temporal.asOf() | displayDate:'dateTime' }}</div>
      <button pButton type="button" class="p-button-outlined p-button-sm back-today" (click)="backToToday()">
        {{ 'temporal.panel.backToday' | translate }}
      </button>
    </div>
  }

  <!-- Change-density strip — own state chain; failure degrades to picker-only -->
  @switch (state()) {
    @case ('loading') {
      <div class="strip-loading">{{ 'temporal.panel.loading' | translate }}</div>
    }
    @case ('ready') {
      @if (bars().length > 0) {
        <!-- The HEADER is the section's label and its chart ↔ table switch, the same in both views (the switch lives
             here, not inside the chart: the table view scrolls).
             The CHART shows ONE year, the year of the month the calendar is on: twelve named months that always
             fit, so nothing scrolls sideways. What belongs to the chart stands on one line over its bars — the year
             it shows, between two arrows, and a caption naming the calendar's month (or the bar under the pointer)
             with its count. In one row with the switch, the switch read as part of the caption's sentence.
             The LIST holds the whole history, newest first, so that line leaves with the chart; a row turns the
             calendar to its month, as a bar does. -->
        <div class="strip-block">
          <div class="strip-header">
            <span class="section-label" [id]="stripLabelId">{{ 'temporal.panel.stripLabel' | translate }}</span>
            <app-chart-view-switch [(table)]="stripTable" [labelledBy]="stripLabelId" />
          </div>
          @if (!stripTable()) {
            <div class="strip-subhead">
              <!-- The year between two arrows, drawn as the calendar under it draws its month's. -->
              <div class="year-stepper">
                <button pButton type="button" class="p-button-text p-button-secondary p-button-rounded year-step" [disabled]="!canStepBack()" (click)="stepYear(-1)"
                  [attr.aria-label]="'controls.calendar.previousYear' | translate"><fa-duotone-icon [icon]="faChevronLeft" /></button>
                <span class="year-label">{{ shownMonth().year }}</span>
                <button pButton type="button" class="p-button-text p-button-secondary p-button-rounded year-step" [disabled]="!canStepForward()" (click)="stepYear(1)"
                  [attr.aria-label]="'controls.calendar.nextYear' | translate"><fa-duotone-icon [icon]="faChevronRight" /></button>
              </div>
              <!-- Every caption of the year is laid out here unseen, under the one in view: the box keeps the
                   widest one's width, so naming the bar under the pointer never changes the line. -->
              <span class="strip-caption">
                @for (text of captions(); track $index) {
                  <span class="strip-caption-size" aria-hidden="true">{{ text }}</span>
                }
                <span class="strip-caption-text">{{ caption() }}</span>
              </span>
            </div>
          }
          <div class="strip-section" [class.table-open]="stripTable()">
            <app-bar-timeline [bars]="bars()" [interactive]="true" [(showTable)]="stripTable" [height]="52"
              [scaleMax]="scaleMax()" [detailColumn]="false"
              [tableLabels]="{ period: ('temporal.panel.tableMonth' | translate), count: ('temporal.panel.tableChanges' | translate) }"
              [ariaLabel]="stripTable() ? ('temporal.panel.stripLabel' | translate) : ('temporal.panel.stripAria' | translate: { year: shownMonth().year })"
              (barSelect)="onBarSelect($event)" (barPreview)="onBarPreview($event)" />
          </div>
        </div>
      } @else {
        <div class="strip-empty">{{ 'temporal.panel.noHistory' | translate }}</div>
      }
    }
  }

  <!-- Instant picker + quick picks.
       The calendar keeps the size it has in every date field, and the jumps into the past stand beside it as
       their own labelled group; where the two do not fit one row, the group drops under the calendar. -->
  <div class="picker-section">
    <div class="picker-label">
      <span class="section-label">{{ 'temporal.panel.pickDayLabel' | translate }}</span>
      <button type="button" class="section-info-trigger" (click)="honestyPopover.toggle($event)"
              [attr.aria-label]="'common.moreInfo' | translate">
        <fa-duotone-icon [icon]="faCircleInfo" />
      </button>
    </div>
    <div class="picker-body">
      <!-- appDatepickerEscapeTag: an inline picker has no overlay, yet its day/month/year cells still
           claim every Escape — the tag marks those claims hollow so Escape from inside the calendar
           still closes this panel instead of going dead.
           appInlineCalendarKeys: the library keeps a calendar's days reachable by keyboard for a popup alone — the
           directive gives this one its Tab stop and keeps the focus through a change of month.
           The click and keydown listeners sit on the calendar's host: after any gesture inside it the panel reads
           which month it shows, so the strip above follows the calendar's own arrows and its month and year views. -->
      <p-datepicker appDatepickerEscapeTag appInlineCalendarKeys [inline]="true" [ngModel]="selectedDay()" (ngModelChange)="selectedDay.set($event)"
        [minDate]="minDate()" [maxDate]="today()" [defaultDate]="viewDate()"
        (click)="syncShownMonth()" (keydown)="syncShownMonth()" />
      <div class="quick-picks" role="group" [attr.aria-labelledby]="quickPicksLabelId">
        <span class="section-label" [id]="quickPicksLabelId">{{ 'temporal.panel.jumpBack' | translate }}</span>
        <button type="button" class="quick-pick" (click)="applyPreset(1)">
          {{ 'temporal.panel.presets.oneMonth' | translate }}
        </button>
        <button type="button" class="quick-pick" (click)="applyPreset(6)">
          {{ 'temporal.panel.presets.sixMonths' | translate }}
        </button>
        <button type="button" class="quick-pick" (click)="applyPreset(12)">
          {{ 'temporal.panel.presets.oneYear' | translate }}
        </button>
      </div>
    </div>
  </div>

  <!-- Go — enter() carries the landing rule (stay on content spine, else /manual).
       Footer grammar (QA round 4): the honesty note sits as quiet meta LEFT of the action —
       it bounds what "this day" can be.
       The button NAMES the day it opens: the pick outlives the month it was made in (the calendar's arrows, a bar and
       a year arrow all turn the calendar away from it), and "View this day" over a month with no day marked opened a
       day nothing on screen showed. -->
  <div class="go-row">
    @if (state() === 'ready' && activity(); as a) {
      <div class="honesty-note">{{ 'temporal.panel.historySince' | translate: { date: (a.historySince | displayDate:'medium') } }}</div>
    }
    <button pButton type="button" class="go-btn" [disabled]="!canGo()" (click)="go()">
      @if (selectedDay(); as day) {
        {{ 'temporal.panel.goDay' | translate: { date: (day | displayDate:'medium') } }}
      } @else {
        {{ 'temporal.panel.go' | translate }}
      }
    </button>
  </div>

  <p-popover #honestyPopover styleClass="section-info-popover">
    <div class="section-info-body">{{ 'temporal.panel.honestyNote' | translate }}</div>
  </p-popover>
</div>
